import { useCallback, useMemo, useState } from 'react';
import { FormSpy } from 'react-final-form';
import { getIn } from 'final-form';
import { isValid } from 'date-fns';

import { parseProductSalesFormDate } from 'KnmErknmPackage/MainModule/common/productSalesSuspensionDates';
import { isProductSalesSuspensionLocked } from 'KnmErknmPackage/MainModule/common/getErknmProductSalesSuspensionValidate';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { Button } from 'UIPackage/MainModule/components/Button';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';

import styles from './ErknmProductSalesResumption.module.scss';
import { KnmAttachmentsSigning } from '../../../../KnmPackage/MainModule/components/KnmAttachmentsSigning';
import { KnmCollapse } from '../../../../KnmPackage/MainModule/components/KnmCollapse';

const RESUMPTION_HINT = 'Для отмены предписания сохраните внесённые сведения и подпишите паспорт КНМ.';
const ACCEPTED_EXTENSIONS = ['pdf', 'docx', 'zip', 'odt', 'doc'];
const ACCEPTED_FILES = ACCEPTED_EXTENSIONS.map(ext => `.${ext}`).join(',');

interface IErknmProductSalesResumptionProps {
  formRef: IKnmFormRef | undefined;
  id: string;
  isWatchOnly: boolean;
  name: string;
  value: IKnmErknmFormResultDecision;
}

export const ErknmProductSalesResumption = ({
  formRef,
  id,
  isWatchOnly,
  name,
  value,
}: IErknmProductSalesResumptionProps) => {
  const resumptionName = `${name}.productSalesResumption`;
  const documentsName = `${resumptionName}.documents`;
  const blockId = `${id}.productSalesResumption`;

  const [isVisibleSignModal, setVisibleSignModal] = useState(false);
  const [isDocumentsTouched] = useState<boolean>(false);

  const resumption = value.productSalesResumption;
  const isPrescriptionSigned = isProductSalesSuspensionLocked(value);
  const isResumptionSigned = !!value.resumptionSigned;
  const isOpened = resumption != null || isResumptionSigned;
  const isEditable = !isWatchOnly && isPrescriptionSigned && !isResumptionSigned;

  const dateDecision = parseProductSalesFormDate(value.dateDecision);

  const minDate = useMemo(() => {
    if (isResumptionSigned || !dateDecision || !isValid(dateDecision)) {
      return undefined;
    }

    return dateDecision;
  }, [isResumptionSigned, dateDecision?.getTime()]);

  const handleOpen = useCallback(() => {
    formRef?.current?.change(
      resumptionName as keyof IKnmForm,
      { resumptionDate: undefined, decisionText: undefined, documents: [] } as never,
    );
  }, [formRef, resumptionName]);

  const handleRemoveResumption = useCallback(() => {
    if (!isEditable) return;

    formRef?.current?.change(resumptionName as keyof IKnmForm, undefined as never);
  }, [formRef, isEditable, resumptionName]);

  const handleAddClick = useCallback(() => setVisibleSignModal(true), []);
  const handleCloseSignModal = useCallback(() => setVisibleSignModal(false), []);

  return (
    <div className={styles.Block}>
      <FieldBlock
        label="Сведения о возобновлении реализации продукции"
        id={`${blockId}.section`}
        button={
          !isOpened ? (
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              disabled={!isEditable || !!value.isNew}
              onClick={handleOpen}
            />
          ) : undefined
        }
      >
        {isOpened && (
          <KnmCollapse
            id={blockId}
            title="Сведения о возобновлении реализации продукции"
            openByDefault
            onClose={isEditable ? handleRemoveResumption : undefined}
          >
            <>
              <Row>
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: 'Дата возобновления реализации продукции',
                    field: `${resumptionName}.resumptionDate`,
                    isKnm: true,
                  })}
                  color="black"
                  size="half"
                  id={`${blockId}.resumptionDate`}
                >
                  <DatePickerField name={`${resumptionName}.resumptionDate`} disabled={!isEditable} minDate={minDate} />
                </FieldBlock>
              </Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: 'Решение о возобновлении реализации продукции',
                  field: `${resumptionName}.decisionText`,
                  isKnm: true,
                })}
                color="black"
                id={`${blockId}.decisionText`}
                margin="bottom"
              >
                <TextareaField
                  name={`${resumptionName}.decisionText`}
                  disabled={!isEditable}
                  resize="vertical"
                  placeholder="Основание отмены или отзыва предписания, номер и дата решения о возобновлении реализации продукции"
                />
              </FieldBlock>
              <FormSpy subscription={{ errors: true, submitFailed: true }}>
                {({ errors, submitFailed }) => {
                  const documentsError = getIn(errors || {}, documentsName) as string | undefined;
                  const isDocumentsInvalid = !!documentsError && (submitFailed || isDocumentsTouched);

                  return (
                    <div id={`${blockId}.documents`}>
                      <div className="shared-group-label">
                        {getTitleFieldWithRedStar({
                          label: 'Документы',
                          field: documentsName,
                          isKnm: true,
                        })}
                        <span className="shared-no-wrap-text">
                          <Button
                            className="shared-btn-add"
                            text="Добавить"
                            variant="link"
                            color="primary"
                            type="button"
                            disabled={!isEditable}
                            onClick={handleAddClick}
                          />
                        </span>
                      </div>
                      <KnmAttachmentsSigning
                        name={documentsName}
                        formRef={formRef}
                        isWatchOnly={!isEditable}
                        isVisibleSignModal={isVisibleSignModal}
                        closeSignModal={handleCloseSignModal}
                        acceptedFiles={ACCEPTED_FILES}
                        isShowDateTime={true}
                      />
                      {isDocumentsInvalid && <p className="shared-input-error">{documentsError}</p>}
                    </div>
                  );
                }}
              </FormSpy>
              {!isResumptionSigned && <Hint text={RESUMPTION_HINT} margin="top" />}
            </>
          </KnmCollapse>
        )}
      </FieldBlock>
    </div>
  );
};
